import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format } from 'date-fns';
import { CREATE_PROJECT, UPDATE_PROJECT } from '../../graphql/mutations';
import { GET_PROJECTS, GET_PROJECT } from '../../graphql/queries';
import { useAccount } from '../../context/AccountContext';
import { showInkToast } from '../cms/InkToast';
import { calendarDate, dateValue } from '../reach/reach';

interface Project {
  id: string;
  name: string;
  plan?: string;
  deadline: string;
  campaignStory?: string;
  landingPageUrl?: string;
}

interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  project?: Project | null; // If provided, modal is in edit mode
  onSubmit?: (project: {
    name: string;
    plan: string;
    deadline: Date | undefined;
    campaignStory: string;
    landingPageUrl: string;
  }) => void;
}

/**
 * New campaign / Edit campaign (Reach → Campaigns, and the posts manager).
 * The end date is a calendar day: sent as "2026-10-31" and read back as that
 * day (calendarDate), so a person west of UTC never sees it a day early.
 */
export function CreateProjectModal({ open, onClose, project, onSubmit }: CreateProjectModalProps) {
  const isEditMode = !!project;
  const [pickerOpen, setPickerOpen] = useState(false);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [name, setName] = useState('');
  const [plan, setPlan] = useState('');
  const [deadline, setDeadline] = useState<Date>();
  const [campaignStory, setCampaignStory] = useState('');
  const [landingPageUrl, setLandingPageUrl] = useState('');

  const { accountId } = useAccount();

  // Populate fields when in edit mode
  useEffect(() => {
    if (project && open) {
      setName(project.name || '');
      setPlan(project.plan || '');
      setDeadline(calendarDate(project.deadline) || undefined);
      setCampaignStory(project.campaignStory || '');
      setLandingPageUrl(project.landingPageUrl || '');
    } else if (!project && open) {
      // Reset fields when creating new project
      setName('');
      setPlan('');
      setDeadline(undefined);
      setCampaignStory('');
      setLandingPageUrl('');
    }
  }, [project, open]);

  const [createProject, { loading: creating }] = useMutation(CREATE_PROJECT, {
    refetchQueries: [{ query: GET_PROJECTS, variables: { accountId } }],
    onCompleted: () => {
      showInkToast('Campaign created.');
      handleClose();
      if (onSubmit) {
        onSubmit({
          name,
          plan,
          deadline,
          campaignStory,
          landingPageUrl,
        });
      }
    },
    onError: (error) => {
      showInkToast(`The campaign wasn’t created: ${error.message}`);
    },
  });

  const [updateProject, { loading: updating }] = useMutation(UPDATE_PROJECT, {
    refetchQueries: [
      { query: GET_PROJECTS, variables: { accountId } },
      { query: GET_PROJECT, variables: { id: project?.id } },
    ],
    onCompleted: () => {
      showInkToast('Campaign saved.');
      handleClose();
      if (onSubmit) {
        onSubmit({
          name,
          plan,
          deadline,
          campaignStory,
          landingPageUrl,
        });
      }
    },
    onError: (error) => {
      showInkToast(`The campaign wasn’t saved: ${error.message}`);
    },
  });

  const loading = creating || updating;

  // A new campaign can't end in the past; an existing one keeps the date it has.
  const pastDate = !!deadline && deadline.getTime() < today.getTime();
  const dateBlocked =
    pastDate && !(isEditMode && calendarDate(project?.deadline)?.getTime() === deadline?.getTime());

  const handleSubmit = () => {
    if (!deadline || dateBlocked) return;

    if (isEditMode && project) {
      // Update existing project
      updateProject({
        variables: {
          id: project.id,
          input: {
            name,
            plan: plan || undefined,
            deadline: dateValue(deadline),
            campaignStory: campaignStory || undefined,
            landingPageUrl: landingPageUrl || undefined,
          },
        },
      });
    } else {
      // Create new project
      if (!accountId) return;
      createProject({
        variables: {
          input: {
            accountId,
            name,
            plan: plan || undefined,
            deadline: dateValue(deadline),
            status: 'planning',
            progress: 0,
            campaignStory: campaignStory || undefined,
            landingPageUrl: landingPageUrl || undefined,
          },
        },
      });
    }
  };

  const handleClose = () => {
    setName('');
    setPlan('');
    setDeadline(undefined);
    setCampaignStory('');
    setLandingPageUrl('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-core-dark text-white">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit campaign' : 'New campaign'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Change the name, brief, end date or landing page.'
              : 'Name it, say what it’s for and when it ends. You add posts after.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Project Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-form-dark text-cmp-ink"
            />
          </div>

          {/* Project Brief */}
          <div className="space-y-2">
            <Label htmlFor="campaignStory">Brief *</Label>
            <Textarea
              id="campaignStory"
              placeholder="Brief"
              value={campaignStory}
              onChange={(e) => setCampaignStory(e.target.value)}
              className="min-h-32 resize-none bg-form-dark text-cmp-ink"
            />
            <p className="descriptor-text">
              This helps your team understand the campaign's objectives and messaging.
            </p>
          </div>

          {/* Deadline */}
          <div className="space-y-2">
            <Label>End date *</Label>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal bg-form-dark text-cmp-ink"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {deadline ? format(deadline, 'MMM d, yyyy') : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="!w-fit p-2 bg-core-dark" align="start">
                <Calendar
                  mode="single"
                  selected={deadline}
                  onSelect={(d) => {
                    setDeadline(d);
                    // Picked: close, so Cancel and Create aren't covered.
                    if (d) setPickerOpen(false);
                  }}
                  disabled={{ before: today }}
                  initialFocus
                  className="p-2"
                  classNames={{
                    cell: 'flex-shrink-0 w-9 px-0.5',
                    day: 'flex-shrink-0 w-9 h-9',
                    head_cell: 'flex-shrink-0 w-9 px-0.5',
                    row: 'gap-0.5',
                    table: 'gap-0',
                  }}
                />
              </PopoverContent>
            </Popover>
            {dateBlocked ? <p className="text-xs text-error">Pick today or a later day.</p> : null}
          </div>

          {/* Landing Page URL */}
          <div className="space-y-2">
            <Label htmlFor="landingPage">Landing page URL (optional)</Label>
            <Input
              id="landingPage"
              type="url"
              placeholder="https://example.com/page"
              value={landingPageUrl}
              onChange={(e) => setLandingPageUrl(e.target.value)}
              className="bg-form-dark text-cmp-ink"
            />
            <p className="descriptor-text">
              Link to the landing page associated with this campaign.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name || !campaignStory || !deadline || dateBlocked || loading}
          >
            {loading
              ? isEditMode
                ? 'Saving…'
                : 'Creating…'
              : isEditMode
                ? 'Save changes'
                : 'Create campaign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
